import { Modal, Text, HStack, Box, Pressable, Image, ScrollView } from 'native-base'
import { useState, useEffect } from 'react'
import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ArrowsOut, Question, PenNib, X } from '@phosphor-icons/react'
import { Package } from '@consumer/graphql'
import i18n from '../../i18n'

// ─── Shared sub-components ───────────────────────────────────────────────────

interface PinDigitBoxProps {
  digit: string
}

const PinDigitBox = ({ digit }: PinDigitBoxProps) => (
  <Box backgroundColor="#BFDBFE" borderRadius="6px" width={8} height={9} justifyContent="center" alignItems="center">
    <Text color="#1E40AF" fontWeight="700" fontSize={16}>
      {digit}
    </Text>
  </Box>
)

// ─── Progress dots ────────────────────────────────────────────────────────────

interface StepDotsProps {
  total: number
  current: number
}

const StepDots = ({ total, current }: StepDotsProps) => (
  <HStack space={2} justifyContent="center">
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        width={2}
        height={2}
        borderRadius="full"
        backgroundColor={i === current ? '#C0392B' : 'transparent'}
        borderWidth={1}
        borderColor="#C0392B"
      />
    ))}
  </HStack>
)

// ─── Step definitions ─────────────────────────────────────────────────────────

interface StepConfig {
  heading: string
  body: string
  bodyNode?: ReactElement
  Illustration: () => ReactElement
  buttonLabel: string
}

const STEPS: StepConfig[] = [
  {
    heading: "You'll receive your PIN day of delivery.",
    body: "On the morning of your delivery, we'll text and email you a secure 4-digit PIN.",
    Illustration: () => (
      <Image
        source={{ uri: 'pin-instruction.png' }}
        resizeMode="contain"
        w="full"
        h={220}
        mb={4}
        alt="Pin Instructions"
      />
    ),
    buttonLabel: i18n.t('common:NEXT'),
  },
  {
    heading: 'Ensure a secure handoff via a PIN',
    body: 'Only give your PIN to the driver face-to-face.',
    Illustration: () => (
      <Image source={{ uri: 'pin-share.png' }} resizeMode="contain" w="full" h={220} mb={4} alt="Pin Share" />
    ),
    buttonLabel: i18n.t('common:NEXT'),
  },
  {
    heading: "Can't be there? Designate a recipient.",
    body: "Tap 'Add Recipient' to let someone you trust receive the delivery for you.",
    Illustration: () => (
      <Image source={{ uri: 'pin-use.png' }} resizeMode="contain" w="full" h={220} mb={4} alt="Pin Use" />
    ),
    buttonLabel: 'Got it!',
  },
]

const AUTHD_STEPS: StepConfig[] = [
  {
    heading: 'Provide your PIN in person.',
    body: 'Only give your PIN to the driver face-to-face.',
    bodyNode: (
      <Text fontSize={14} color="#374151" textAlign="center" mb={6} lineHeight={22}>
        Only give your PIN to the driver face-to-face. <Text fontWeight="700">Never</Text> send it to a driver by
        message.
      </Text>
    ),
    Illustration: () => (
      <Image source={{ uri: 'pin-share.png' }} resizeMode="contain" w="full" h={220} mb={4} alt="Pin Share" />
    ),
    buttonLabel: i18n.t('common:NEXT'),
  },
  {
    heading: "Can't be there? Designate a recipient.",
    body: "Tap 'Add Recipient' to let someone you trust receive the delivery for you.",
    Illustration: () => (
      <Image source={{ uri: 'pin-use.png' }} resizeMode="contain" w="full" h={220} mb={4} alt="Pin Use" />
    ),
    buttonLabel: 'Got it!',
  },
]

const EYEBROW = 'HOW SIGNATURE BY PIN WORKS'

// ─── How It Works Modal ───────────────────────────────────────────────────────

interface PinHowItWorksModalProps {
  visible: boolean
  onClose: () => void
  steps: StepConfig[]
}

const PinHowItWorksModal = ({ visible, onClose, steps }: PinHowItWorksModalProps) => {
  const [currentStep, setCurrentStep] = useState(0)

  const handleClose = () => {
    onClose()
    setTimeout(() => setCurrentStep(0), 300)
  }

  const handleNext = () => {
    if (currentStep === steps.length - 1) {
      handleClose()
      return
    }
    setCurrentStep(currentStep + 1)
  }

  const step = steps[currentStep]
  const { Illustration } = step

  return (
    <Modal isOpen={visible} onClose={handleClose}>
      <Modal.Content maxWidth="360px" maxH="90%" borderRadius="16px">
        <ScrollView bounces={false}>
          <Box px={5} pt={5} pb={6}>
            {/* Header row: close button, centred dots, and spacer */}
            <HStack justifyContent="space-between" alignItems="center" mb={4}>
              <Pressable onPress={handleClose} p={1} accessibilityLabel="Close modal">
                <X size={20} color="#374151" />
              </Pressable>
              <StepDots total={steps.length} current={currentStep} />
              {/* Spacer mirrors the close button to keep dots visually centred */}
              <Box width={6} />
            </HStack>

            {/* Eyebrow label */}
            <Text fontSize={10} fontWeight="700" color="#C0392B" letterSpacing={1} textTransform="uppercase" mb={2}>
              {EYEBROW}
            </Text>

            {/* Step heading */}
            <Text fontSize={20} fontWeight="700" color="#111827" mb={4} lineHeight={28}>
              {step.heading}
            </Text>

            {/* Illustration */}
            <Illustration />

            {/* Body copy — use bodyNode when present for inline rich text */}
            {step.bodyNode ?? (
              <Text fontSize={14} color="#374151" textAlign="center" mb={6} lineHeight={22}>
                {step.body}
              </Text>
            )}

            {/* CTA */}
            <Pressable
              onPress={handleNext}
              backgroundColor="#C0392B"
              borderRadius="full"
              py={3}
              alignItems="center"
              accessibilityRole="button"
            >
              <Text color="white" fontWeight="700" fontSize={16}>
                {step.buttonLabel}
              </Text>
            </Pressable>
          </Box>
        </ScrollView>
      </Modal.Content>
    </Modal>
  )
}

// ─── PIN Display Modal ────────────────────────────────────────────────────────

interface PinDisplayModalProps {
  pin: string
  visible: boolean
  onClose: () => void
}

const PinDisplayModal = ({ pin, visible, onClose }: PinDisplayModalProps) => {
  const [copied, setCopied] = useState(false)

  const handleClose = () => {
    setCopied(false)
    onClose()
  }

  const handleAddRecipient = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My delivery PIN', text: `Your delivery PIN is: ${pin}` })
      } else {
        await navigator.clipboard.writeText(pin)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') throw err
    }
  }

  return (
    <Modal isOpen={visible} onClose={handleClose}>
      <Modal.Content maxWidth="360px" borderRadius="16px" backgroundColor="#DCEBF7">
        <Box px={6} pt={5} pb={6}>
          <HStack justifyContent="flex-end" mb={4}>
            <Pressable onPress={handleClose} p={1} accessibilityLabel="Close modal">
              <X size={20} color="#374151" />
            </Pressable>
          </HStack>

          <Text
            fontSize={10}
            fontWeight="700"
            color="#374151"
            letterSpacing={1}
            textTransform="uppercase"
            textAlign="center"
            mb={2}
          >
            YOUR DELIVERY PIN
          </Text>

          <Text fontSize={20} fontWeight="700" color="#111827" textAlign="center" mb={6} lineHeight={28}>
            Provide this to your driver on delivery.
          </Text>

          {/* Single large number — not individual digit boxes */}
          <Text
            fontSize={80}
            fontWeight="700"
            color="#111827"
            textAlign="center"
            letterSpacing={window.innerWidth < 400 ? 4 : 8}
            mb={6}
            lineHeight={88}
          >
            {pin}
          </Text>

          <Box borderBottomWidth={1} borderColor="#CBD5E1" mb={4} />

          <Text fontSize={14} color="#374151" textAlign="center" mb={4}>
            Won't be around? Designate a recipient.
          </Text>

          <Pressable
            onPress={handleAddRecipient}
            backgroundColor="#111827"
            borderRadius="full"
            py={3}
            alignItems="center"
            accessibilityRole="button"
          >
            <Text color="white" fontWeight="700" fontSize={16}>
              {copied ? 'Copied!' : 'Add Recipient'}
            </Text>
          </Pressable>
        </Box>
      </Modal.Content>
    </Modal>
  )
}

// ─── Main exported component ──────────────────────────────────────────────────

export const PackageRequiredPinStatus = ({ pkg }: { pkg: Package }) => {
  const [searchParams] = useSearchParams()
  const [infoModalVisible, setInfoModalVisible] = useState<boolean>(false)
  const [pinModalVisible, setPinModalVisible] = useState<boolean>(false)

  useEffect(() => {
    if (searchParams.get('pinInstructions') === 'true') {
      setInfoModalVisible(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!pkg.requiresSignatureByPin) return null

  const pin = pkg.pin

  return (
    <>
      <PinHowItWorksModal
        visible={infoModalVisible}
        onClose={() => setInfoModalVisible(false)}
        steps={pin ? AUTHD_STEPS : STEPS}
      />
      {pin && <PinDisplayModal pin={pin} visible={pinModalVisible} onClose={() => setPinModalVisible(false)} />}

      {pin ? (
        <Box
          backgroundColor="#DCEBF7"
          borderRadius="8px"
          px={3}
          py={3}
          mx={2}
          mb={3}
          flexDirection="row"
          alignItems="center"
        >
          <Text fontWeight="600" fontSize={14} flexShrink={0} mr={2} numberOfLines={1}>
            Your delivery PIN
          </Text>
          <HStack space={1} flex={1}>
            {pin.split('').map((digit, i) => (
              <PinDigitBox key={`${digit}-${i}`} digit={digit} />
            ))}
          </HStack>
          <Pressable onPress={() => setPinModalVisible(true)} p={1}>
            <ArrowsOut size={22} color="#1E40AF" />
          </Pressable>
        </Box>
      ) : (
        <Box
          backgroundColor="#EEC055"
          borderRadius="8px"
          px={3}
          py={3}
          mx={2}
          mb={3}
          flexDirection="row"
          alignItems="center"
        >
          <Box flexShrink={0} mr={2}>
            <PenNib size={20} color="#92400E" />
          </Box>
          <Text color="#92400E" fontWeight="500" fontSize={14} flex={1}>
            This Order Requires{' '}
            <Text color="#92400E" fontWeight="700" fontSize={14}>
              Signature by PIN
            </Text>
          </Text>
          <Pressable onPress={() => setInfoModalVisible(true)} ml={2} p={1}>
            <Question size={20} color="#92400E" />
          </Pressable>
        </Box>
      )}
    </>
  )
}
